import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './vatsApi';
import type { Vat, Account } from './vatsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    vatId: number | null;
}

export function VatView({ vatId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<Vat | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<Vat | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const accs = await api.getAccounts();
            setAccounts(accs);
            if (vatId === null) {
                setItem({ id: 0, vatCode: '', vatName: '', vatRate: 0, account: null });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getVat(vatId);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [vatId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isVatCodeValid = !!item.vatCode;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew ? await api.createVat(item) : await api.updateVat(item);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (!window.confirm(t('vat.confirmDelete'))) return;
        setSaving(true); setError(null);
        try {
            await api.deleteVat(item.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof Vat>(field: K, value: Vat[K]) => {
        setItem({ ...item, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.vatCode ?? ''} ${item.vatName ?? ''}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={isVatCodeValid}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={del}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('vat.vatCode')}>
                        {!isNew && !isEditing ? (
                            <Typography sx={sx14}>{item.vatCode}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                value={item.vatCode}
                                onChange={e => update('vatCode', e.target.value.toUpperCase())}
                                error={!isVatCodeValid} />
                        )}
                    </FormRow>

                    <FormRow label={t('vat.vatName')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.vatName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                value={item.vatName ?? ''}
                                onChange={e => update('vatName', e.target.value)} />
                        )}
                    </FormRow>

                    <FormRow label={t('vat.vatRate')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>
                                {item.vatRate != null ? `${(item.vatRate * 100).toLocaleString()} %` : ''}
                            </Typography>
                        ) : (
                            <TextField size="small" type="number" fullWidth
                                value={item.vatRate ?? 0}
                                onChange={e => update('vatRate', e.target.value === '' ? 0 : Number(e.target.value))} />
                        )}
                    </FormRow>

                    <FormRow label={t('vat.account')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>
                                {item.account ? `${item.account.accountNumber} ${item.account.accountName}` : ''}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.account?.id ?? ''}
                                    onChange={e => {
                                        const a = accounts.find(x => x.id === e.target.value);
                                        update('account', a ?? null);
                                    }}
                                >
                                    {[...accounts]
                                        .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                                        .map(a => (
                                            <MenuItem key={a.id} value={a.id}>{a.accountNumber} {a.accountName}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
